import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function RecipePrint() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const printStarted = useRef(false);
  const printTimer = useRef(null);
  const autoPrint = searchParams.get("auto") === "1";

  const scheduleAutoPrint = useCallback(() => {
    if (!autoPrint || printStarted.current) return;
    printStarted.current = true;
    printTimer.current = window.setTimeout(() => window.print(), 350);
  }, [autoPrint]);

  useEffect(() => {
    api.get(`/recipes/${id}`)
      .then(({ data }) => setRecipe(data))
      .catch(() => setError("No hemos podido preparar esta receta para imprimir."))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!recipe) return;
    if (!recipe.image_url) scheduleAutoPrint();
    const timer = window.setTimeout(scheduleAutoPrint, 2500);
    return () => window.clearTimeout(timer);
  }, [recipe, scheduleAutoPrint]);

  useEffect(() => () => {
    if (printTimer.current) window.clearTimeout(printTimer.current);
  }, []);

  if (loading) {
    return <main className="recipe-print-screen" data-testid="recipe-print-loading">Preparando receta…</main>;
  }
  if (error || !recipe) {
    return (
      <main className="recipe-print-screen" data-testid="recipe-print-error">
        <p>{error || "Receta no encontrada."}</p>
        <Link to="/app" className="print-toolbar-link" data-testid="recipe-print-error-back-link">Volver al recetario</Link>
      </main>
    );
  }

  const sourceUrl = recipe.source_url || recipe.source_reference_url;
  return (
    <main className="recipe-print-screen" data-testid="recipe-print-page">
      <div className="print-toolbar" data-print-hidden="true">
        <Link to={`/app/recipe/${recipe.id}`} className="print-toolbar-link" data-testid="recipe-print-back-link">
          <ArrowLeft size={17} /> Volver a la receta
        </Link>
        <button type="button" onClick={() => window.print()} className="print-toolbar-button" data-testid="recipe-print-button">
          <Printer size={18} /> Imprimir / Guardar como PDF
        </button>
      </div>

      <article className="recipe-print-document">
        <header className="recipe-print-header">
          <p className="recipe-print-brand">MIZETA</p>
          <h1 data-testid="recipe-print-title">{recipe.title}</h1>
          {recipe.description && <p className="recipe-print-description" data-testid="recipe-print-description">{recipe.description}</p>}
          <div className="recipe-print-meta" data-testid="recipe-print-meta">
            {recipe.servings && <span><strong>Raciones</strong>{recipe.servings}</span>}
            {recipe.total_time && <span><strong>Tiempo</strong>{recipe.total_time}</span>}
            {recipe.difficulty && <span><strong>Dificultad</strong>{recipe.difficulty}</span>}
          </div>
        </header>

        {recipe.image_url && (
          <img
            src={recipe.image_url}
            alt={`Portada de ${recipe.title}`}
            className="recipe-print-cover"
            data-testid="recipe-print-cover"
            onLoad={scheduleAutoPrint}
            onError={(event) => { event.currentTarget.hidden = true; scheduleAutoPrint(); }}
          />
        )}

        <div className="recipe-print-grid">
          <section className="recipe-print-section" data-testid="recipe-print-ingredients">
            <h2>Ingredientes</h2>
            <ul>
              {recipe.ingredients.map((ingredient, index) => (
                <li key={`${ingredient.name}-${index}`}>
                  <strong>{ingredient.quantity ? `${ingredient.quantity} ` : ""}</strong>{ingredient.name}
                  {ingredient.notes && <small> — {ingredient.notes}</small>}
                </li>
              ))}
            </ul>
          </section>

          <section className="recipe-print-section" data-testid="recipe-print-steps">
            <h2>Procedimiento</h2>
            <ol>
              {recipe.steps.map((step, index) => (
                <li key={`${step.order}-${index}`}>
                  <span>{String(step.order || index + 1).padStart(2, "0")}</span>
                  <p>{step.text}</p>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {recipe.tips?.length > 0 && (
          <section className="recipe-print-notes" data-testid="recipe-print-notes">
            <h2>Notas y trucos</h2>
            <ul>{recipe.tips.map((tip, index) => <li key={index}>{tip}</li>)}</ul>
          </section>
        )}

        {sourceUrl && (
          <footer className="recipe-print-source" data-testid="recipe-print-source">
            <span>Fuente original</span>
            <a href={sourceUrl}>{sourceUrl}</a>
          </footer>
        )}
      </article>
    </main>
  );
}